import { ArrowLeft } from "lucide-react";
import { ANALYTICS_EVENTS, trackEvent } from "@repo/ui/lib/analytics";
import { AskTigerSparkle } from "./AskTigerSparkle";
import { categoryViewStyles as s } from "./CategoryView.styles";
import type { CategoryViewProps } from "./CategoryView.types";
export type { CategoryId } from "./CategoryView.types";

/**
 * Every category's detail screen. Each one renders through CategoryView, which always ends
 * with the "Or type your own below" hint, so a new category gets it without extra work.
 * Copy stays white-labeled: never name a vendor behind a sub-agent.
 */
export const CATEGORIES: Record<
  import("./CategoryView.types").CategoryId,
  { label: string; description: string; prompts: string[] }
> = {
  oncall: {
    label: "On-Call Lookup",
    description: "Find who's covering",
    // The ten specialties people most often page on call for.
    prompts: [
      "Who's on call for cardiology?",
      "Who's on call for hospital medicine?",
      "Who's on call for general surgery?",
      "Who's on call for orthopedics?",
      "Who's on call for neurology?",
      "Who's on call for anesthesiology?",
      "Who's on call for OB/GYN?",
      "Who's on call for pediatrics?",
      "Who's on call for gastroenterology?",
      "Who's on call for critical care?",
    ],
  },
  support: {
    label: "Customer Support",
    description: "Product questions answered and support tickets created",
    prompts: [
      "How do I set up a broadcast list?",
      "I want to report an issue",
      "Submit an enhancement request",
      "Why didn't my message send?",
    ],
  },
};

export function CategoryView({ category, onBack, onSelectPrompt }: CategoryViewProps) {
  const cat = CATEGORIES[category];

  const handleSelect = (prompt: string, index: number) => {
    trackEvent(ANALYTICS_EVENTS.suggestionChipClicked, {
      source: "category_prompt",
      category,
      prompt,
      index,
    });
    onSelectPrompt(prompt);
  };

  return (
    <div className={s.root}>
      <div className={s.header}>
        <button onClick={onBack} className={s.backBtn}>
          <ArrowLeft className={s.backIcon} /> Back
        </button>
        <p className={s.title}>{cat.label}</p>
        <p className={s.description}>{cat.description}</p>
      </div>
      <div className={s.divider} />
      <div className={s.list}>
        {cat.prompts.map((prompt, index) => (
          <button key={prompt} onClick={() => handleSelect(prompt, index)} className={s.promptBtn}>
            <div className={s.promptInner}>
              <AskTigerSparkle size={12} className={s.promptIcon} />
              <p className={s.promptLabel}>{prompt}</p>
            </div>
          </button>
        ))}
      </div>
      <p className={s.hint}>Or type your own below</p>
    </div>
  );
}
